import type { Building } from "building-types/building/buildingTypes";
import type { Level, LevelAttributes } from "building-types/levels/levelTypes";
import type { SettingsFragment } from "building-types/settings/settingsFragment";
import type { RWTable, Table } from "common/data/table";
import objectKeyFilter from "common/utils/objectKeyFilter";
import { evalFormulas, resolveOptionsInFormula } from "doc/formula/evalFormulas";
import { levelAttributesNew } from "../elements/level/levelTools";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";
import { settingsFromLevel } from "./generateLevelAttributesTools";

function select({
  doc: { optionSets, instanceOptionSets, settings: settingsTable },
  levels,
  optionChoices,
  settings,
}: Building) {
  const definitions = { optionSets, instanceOptionSets };
  return {
    levels,
    settings,
    levelSettingsTable: getLevelSettingsTable(settingsTable, levels),
    optionChoices: resolveOptionsInFormula({ levels }, definitions, optionChoices),
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  settings: "deep",
  levelSettingsTable: "deep",
  optionChoices: "shallow",
});

export function getLevelSettingsTable(
  settings: Table<SettingsFragment>,
  levels: Table<Level>,
): Table<SettingsFragment> {
  const levelUniqueIds: { [key: string]: boolean } = {};
  for (const levelId in levels) {
    levelUniqueIds[levelId] = true;
    // Settings are parented to doc level IDs, so ephemeral levels also need their docLevelId indexed.
    const { docLevelId } = levels[levelId];
    if (docLevelId) {
      levelUniqueIds[docLevelId] = true;
    }
  }

  return objectKeyFilter(settings, (key) => {
    return !!settings[key].parentId && !!levelUniqueIds[settings[key].parentId];
  });
}

function generate({ levels, optionChoices, settings, levelSettingsTable }: ReturnType<typeof select>) {
  const levelsAttributes: RWTable<LevelAttributes> = {};

  for (const levelId in levels) {
    // Level-scoped settings are parented to the doc level ID; ephemeral levels use docLevelId to look them up.
    const { docLevelId } = levels[levelId];
    const settingsLevelId = docLevelId ?? levelId;
    const finalSettings = settingsFromLevel(settingsLevelId, { ...settings }, levelSettingsTable);

    const { windowHeadHeight } = evalFormulas(finalSettings.window, (key) => optionChoices[key]);

    levelsAttributes[levelId] = levelAttributesNew(windowHeadHeight);
  }

  return {
    levelsAttributes,
  };
}

export default memo(select, generate, compare);
